import {
  createContext,
  MutableRefObject,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  DEFAULT_NEXT_ACTION_STATE,
  NextActionHandlers,
  NextActionState,
} from '../../hooks/liveLessonNextAction';

// Cross-cutting concern: the clicker (and, planned, the auto-start
// timer + orange-button labels) needs to read fresh handlers and state
// from components scattered across the live-lesson tree. Prop drilling
// through SlideWrapperViewer → SlideResultViewer → ResponseGridView
// would be painful, so we expose two refs via context. Producers write
// to them in render-effects; the clicker reads from them at keypress
// time. Refs avoid re-rendering the lesson tree on every selected-
// response change.

interface LiveLessonClickerContextValue {
  handlersRef: MutableRefObject<NextActionHandlers>;
  stateRef: MutableRefObject<NextActionState>;
}

const LiveLessonClickerContext = createContext<LiveLessonClickerContextValue | null>(
  null,
);

export function LiveLessonClickerProvider({ children }: { children: ReactNode }) {
  const handlersRef = useRef<NextActionHandlers>({});
  const stateRef = useRef<NextActionState>({ ...DEFAULT_NEXT_ACTION_STATE });
  // Stabilise the context value: useClickerControls lists `ctx` in its
  // effect deps to re-attach the keydown listener, so a fresh object
  // every render would tear down and re-add the global listener every
  // time the provider's parent re-renders.
  const value = useMemo(() => ({ handlersRef, stateRef }), []);
  return (
    <LiveLessonClickerContext.Provider value={value}>
      {children}
    </LiveLessonClickerContext.Provider>
  );
}

export function useLiveLessonClickerContext(): LiveLessonClickerContextValue | null {
  return useContext(LiveLessonClickerContext);
}

// Producer hook: components register their handlers and the ref bag is
// kept fresh on every render. Each call merges into the bag — different
// components own different keys, so they don't step on each other.
//
// Pass a cleanup-keys array if a component should remove its keys on
// unmount (e.g. ResponseGridView clears strength-review keys when the
// teacher navigates away from a results slide).
export function useRegisterClickerHandlers(
  handlers: Partial<NextActionHandlers>,
  cleanupKeys?: ReadonlyArray<keyof NextActionHandlers>,
): void {
  const ctx = useLiveLessonClickerContext();
  useEffect(() => {
    if (!ctx) return;
    Object.assign(ctx.handlersRef.current, handlers);
    if (!cleanupKeys?.length) return;
    return () => {
      for (const key of cleanupKeys) {
        delete ctx.handlersRef.current[key];
      }
    };
  });
}

// Producer hook: same idea, for state. The static lesson state from
// LiveGame is registered here too, so the clicker only ever reads from
// one place.
export function useRegisterClickerLiveState(
  state: Partial<NextActionState>,
  cleanupKeys?: ReadonlyArray<keyof NextActionState>,
): void {
  const ctx = useLiveLessonClickerContext();
  useEffect(() => {
    if (!ctx) return;
    Object.assign(ctx.stateRef.current, state);
    if (!cleanupKeys?.length) return;
    return () => {
      // Reset cleanup keys to their default, so a stale `true` doesn't
      // outlive the component that wrote it.
      const current = (ctx.stateRef.current as unknown) as Record<string, unknown>;
      const defaults = (DEFAULT_NEXT_ACTION_STATE as unknown) as Record<string, unknown>;
      for (const key of cleanupKeys) {
        current[key] = defaults[key];
      }
    };
  });
}
